// Ask Mise — the setup assistant. Two honest modes, never a faked model:
// 1. Once the account exists, questions go to the dashboard's real AI chat
//    endpoint (POST /v1/chat/message, context type 'onboarding') — the same
//    assistant that lives in the dashboard.
// 2. Pasted lists (team, menu, suppliers, address) and the standing FAQs are
//    handled by deterministic local parsers (the design's localTeam/localReply
//    approach) that fill the form directly — instant, offline, no model.
import { Fragment, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { sendChatMessage } from '../../services/chat';
import { money, MODS, price, ROLES, TOOLS, trialEndDate, type TypeKey } from './data';
import type { ChatMsg, MenuLine, Person, SignupState } from './state';
import { authed } from './wiring';

export const STEP_KEYS = ['account', 'business', 'connect', 'review', 'portions', 'ready'];

export const TIPS: Record<number, (s: SignupState) => string> = {
  0: () => 'I’m Mise. I’ll help as you set up. Ask me anything, or paste things like your menu, team list or supplier emails and I’ll fill them in.',
  1: () => 'Not sure which type fits? Tell me how you sell, in a sentence.',
  2: (s) => s.type === 'cpg'
    ? 'Connect where your orders come from. No integrations? Upload your order sheet and I’ll read it.'
    : 'Connect your POS and I’ll bring in your menu and sales. On Toast, I can walk you through the API keys; it takes about 3 minutes.',
  3: () => 'Check these over. You can paste your address, team or suppliers here and I’ll put them in the right place.',
  4: () => 'Everything is on for 14 days, so pick what you’d use first. Ask me what a setup would cost.',
  5: () => 'You’re set up. Start with the short list. I’m here in the dashboard too.',
};

export const CHIPS: Record<number, Array<[string, string?]>> = {
  0: [['Do I need a card?'], ['What does it cost?'], ['Is there a contract?']],
  1: [['Which type am I?'], ['I run a café that also sells wholesale']],
  2: [['Where are my Toast API keys?'], ['Is my data safe?'], ['My POS isn’t listed']],
  3: [['Paste my team', 'Here is my team:\n'], ['Add my suppliers', 'Our suppliers are '], ['Paste address and hours', 'Our address and hours: ']],
  4: [['Which portions do I need?'], ['What would 3 locations cost?']],
  5: [['What should I do first?'], ['How do staff get the app?']],
};

// ---------------------------------------------------------------------------
// Deterministic local parsers — they turn pasted text into form data.
// ---------------------------------------------------------------------------

const PHONE = /(\+?1[\s.-]?)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}/;
const EMAIL = /[^@\s,;]+@[^@\s,;]+\.[^@\s,;]+/;

export function localTeam(text: string): Person[] {
  return text.split(/\n|;/).map((l) => l.trim()).filter(Boolean).map((l) => {
    const phone = (l.match(PHONE) || [''])[0].trim();
    const email = (l.match(EMAIL) || [''])[0].trim();
    const role = ROLES.find((r) => new RegExp('\\b' + r + '\\b', 'i').test(l)) || 'Server';
    const nm = l.replace(PHONE, '').replace(EMAIL, '').replace(new RegExp('\\b' + role + '\\b', 'i'), '')
      .replace(/[^A-Za-zÀ-ÿ' -]/g, ' ').trim().split(/\s+/).filter(Boolean);
    return { first: nm[0] || '', last: nm.slice(1).join(' '), phone, email, role };
  }).filter((p) => p.first);
}

/** "Croissant $4.50" / "Short rib – 32" / "Soup of the day" → menu lines.
 * Lines without a readable price stay flagged — nothing rings up at $0. */
export function localMenu(text: string): MenuLine[] {
  return text.split('\n').map((l) => l.trim()).filter((l) => l && !/^here (is|are) my/i.test(l)).map((l) => {
    const m = l.match(/[\$€£]?\s?(\d{1,4}(?:[.,]\d{1,2})?)\s*$/);
    const priceNum = m ? parseFloat(m[1].replace(',', '.')) : null;
    const name = (m ? l.slice(0, m.index) : l).replace(/[\s.·…\-–—]+$/, '').trim();
    return name ? { name, price: priceNum && priceNum > 0 ? priceNum : null } : null;
  }).filter((x): x is MenuLine => !!x).slice(0, 200);
}

export function localSuppliers(text: string): string[] {
  const after = text.replace(/^.*?(suppliers?|vendors?)\s*(are|:)?/is, '');
  return after.split(/\n|,| and /i).map((x) => x.replace(/[.·]+$/, '').trim())
    .filter((x) => x && x.length < 60).slice(0, 12);
}

export interface AddressBits { street?: string; city?: string; state?: string; zip?: string; phone?: string; hours?: string }
export function localAddress(text: string): AddressBits {
  const out: AddressBits = {};
  const phone = text.match(PHONE); if (phone) out.phone = phone[0].trim();
  const zip = text.match(/\b(\d{5})(?:-\d{4})?\b/); if (zip) out.zip = zip[1];
  const cityStZip = text.match(/([A-Za-zÀ-ÿ' .]+),\s*([A-Z]{2})\s*(\d{5})?/);
  if (cityStZip) { out.city = cityStZip[1].trim(); out.state = cityStZip[2]; if (cityStZip[3]) out.zip = cityStZip[3]; }
  const street = text.match(/\d+\s+[A-Za-zÀ-ÿ0-9' .]+?(?:St|Street|Ave|Avenue|Rd|Road|Blvd|Boulevard|Way|Dr|Drive|Ln|Lane|Pl|Place)\b\.?/i);
  if (street) out.street = street[0].trim();
  const hours = text.match(/\b(?:Mon|Tue|Wed|Thu|Fri|Sat|Sun)[a-z]*[^\n]*\d{1,2}(?::\d{2})?\s*(?:am|pm|AM|PM|[–-])[^\n]*/);
  if (hours) out.hours = hours[0].trim();
  return out;
}

// ---------------------------------------------------------------------------
// Local replies — the standing FAQs (deterministic, from real prices/terms).
// ---------------------------------------------------------------------------

interface LocalResult { t: string; fills?: string[] }

function localFaq(text: string, s: SignupState): LocalResult | null {
  const t = text.toLowerCase();
  if (/card|credit/.test(t)) return { t: `No card. The trial is 14 days with everything on — it ends ${trialEndDate()}. You choose a plan at the end, and we email you 3 days before.` };
  if (/contract/.test(t)) return { t: 'No contract. Month to month; if you stop, it simply stops.' };
  if (/toast/.test(t) && /key|api|credential|find/.test(t)) return { t: 'In Toast Web, open **Integrations › Toast API access › Manage credentials**, then **Create credentials** with read access to orders, menus and restaurant info. Paste the client ID, secret and restaurant GUID into the Toast sheet.' };
  if (/safe|secure|privacy/.test(t)) return { t: 'Mise only reads. It never changes anything in your POS, and you can disconnect any time in Settings.' };
  if (/which type|what type|type am i|café that also|cafe that also/.test(t)) return { t: 'Quick guide: people sit down or pick up at your place → **Restaurant** or **Quick service**. Counter plus coffee or baking → **Café or bakery** (wholesale can be turned on there too). Drinks first → **Bar**. Events and off-site → **Catering**. Packaged products sold to shops or online → **Food brand**.' };
  if (/pos.*(isn|not).*(listed|there)|my pos isn/.test(t)) return { t: 'Use the dashed tile under the POS list — start from a photo, PDF or CSV of your menu. Your iPhone can be the POS, and sales still land in Mise.' };
  if (/staff.*app|how do staff/.test(t)) return { t: 'Each invited person gets an email with a link. They set their own PIN and see their shifts in the Mise app, from the App Store.' };
  if (/do first|start with|first\?$/.test(t) && s.step === 5) return { t: 'Start with the short list under **Needs you** — a few minutes each. The counts one pays off first: with a starting count, Mise can draft your orders.' };
  if (/(cost|price|pay)/.test(t)) {
    if (!s.type) return { t: 'Depends what you run. Base plans: **$199**/location for restaurants, **$99** for bars, **$299** for bakeries, **$499** flat for food brands — portions on top. Pick your type and I’ll price your exact setup. Nothing is charged during the trial.' };
    const m = /(\d+)\s*(location|store|shop|site)/.exec(t);
    const locs = m ? parseInt(m[1], 10) : null;
    const on = s.mods || MODS[s.type].def;
    const pr = price(s.type, on, locs || 1);
    return { t: `With what you’ve picked, about **${money(pr.total)} a month**${locs ? ` for ${locs} ${locs === 1 ? 'location' : 'locations'}` : ''} after the trial. Nothing is charged today.` };
  }
  return null;
}

// ---------------------------------------------------------------------------
// The assistant component
// ---------------------------------------------------------------------------

const md = (t: string): ReactNode =>
  t.split(/(\*\*[^*]+\*\*)/g).map((x, i) =>
    x.startsWith('**')
      ? <b key={i}>{x.slice(2, -2)}</b>
      : x.split('\n').map((y, j) => <Fragment key={i + '-' + j}>{j ? <br /> : null}{y}</Fragment>));

const Ck = ({ s = 12 }: { s?: number }) => (
  <svg width={s} height={s} viewBox="0 0 12 12" fill="none"><path d="M2.5 6.2l2.3 2.3 4.7-5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
);

export interface Draft { text: string; send: boolean; n: number }

interface AssistantProps {
  s: SignupState;
  get: () => SignupState;
  up: (p: Partial<SignupState>) => void;
  draft: Draft | null;
  header: ReactNode;
  onClose?: () => void;
}

export function Assistant({ s, get, up, draft, header, onClose }: AssistantProps) {
  const [txt, setTxt] = useState('');
  const [busy, setBusy] = useState(false);
  const log = useRef<HTMLDivElement>(null);
  const ta = useRef<HTMLTextAreaElement>(null);

  useEffect(() => { if (log.current) log.current.scrollTop = log.current.scrollHeight; }, [s.chat.length, busy]);

  useEffect(() => {
    if (!draft || !draft.n) return;
    if (draft.send) { void send(draft.text); }
    else {
      setTxt(draft.text);
      setTimeout(() => { if (ta.current) { ta.current.focus(); ta.current.setSelectionRange(draft.text.length, draft.text.length); } }, 60);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft && draft.n]);

  /** Structured pastes → the deterministic parsers (no model involved). */
  function tryStructured(text: string): LocalResult | null {
    const t = text.toLowerCase();
    const s0 = get();
    if (/team|staff/.test(t) && (PHONE.test(text) || EMAIL.test(text) || /^here is my team/i.test(text))) {
      const body = text.replace(/^.*?:\s*/s, '');
      const ppl = localTeam(body);
      if (!ppl.length) return { t: 'I couldn’t pick out any names from that. One person per line works best — name, role, mobile, email.' };
      up({ team: [...s0.team, ...ppl] });
      const noEmail = ppl.filter((p) => !p.email).length;
      return {
        t: `Added ${ppl.length} to your team — check the names under Team.${noEmail ? ` ${noEmail} ${noEmail === 1 ? 'has' : 'have'} no email yet; invites go by email.` : ''}`,
        fills: [`${ppl.length} team member${ppl.length === 1 ? '' : 's'}`],
      };
    }
    if (/^here (is|are) my (menu|products)/i.test(text)) {
      const items = localMenu(text.replace(/^.*?:\s*/s, ''));
      if (!items.length) return { t: 'I couldn’t read items out of that. One per line works best — “Croissant $4.50”.' };
      const cur = s0.menu;
      const fresh = items.filter((x) => !cur.some((c) => c.name.toLowerCase() === x.name.toLowerCase()));
      const noPrice = fresh.filter((x) => !x.price).map((x) => x.name);
      up({ menu: [...cur, ...fresh], alt: 'done', altDetail: `${cur.length + fresh.length} items from your message` });
      return {
        t: `Read ${fresh.length} item${fresh.length === 1 ? '' : 's'}.${noPrice.length ? ` No price on: ${noPrice.slice(0, 6).join(', ')}${noPrice.length > 6 ? '…' : ''} — they’re flagged so nothing rings up at $0.` : ' Every one has a price.'}`,
        fills: [`${fresh.length} ${s0.type === 'cpg' ? 'products' : 'menu items'}`],
      };
    }
    if (/supplier|vendor/.test(t)) {
      const names = localSuppliers(text);
      if (!names.length) return { t: 'Tell me the names — “Our suppliers are Sysco, Baldor and Ronnybrook”.' };
      const cur = s0.vendors;
      const add = names.filter((n) => !cur.some((v) => v.name.toLowerCase() === n.toLowerCase())).map((n) => ({ name: n, email: '' }));
      up({ vendors: [...cur, ...add] });
      return { t: `Added ${add.length} supplier${add.length === 1 ? '' : 's'}. Add an order email to any of them and Mise can send orders directly.`, fills: [`${add.length} supplier${add.length === 1 ? '' : 's'}`] };
    }
    if (/address/.test(t) && (/\d{5}/.test(text) || PHONE.test(text))) {
      const bits = localAddress(text);
      const found = Object.keys(bits);
      if (!found.length) return { t: 'I couldn’t read that reliably — the address fields are on the left.' };
      const pl = { ...s0.place };
      if (bits.street) pl.street = bits.street;
      if (bits.city) pl.city = bits.city;
      if (bits.state) pl.state = bits.state;
      if (bits.zip) pl.zip = bits.zip;
      if (bits.phone) pl.phone = bits.phone;
      if (bits.hours) pl.hours = bits.hours;
      pl.src = pl.src || 'chat';
      up({ place: pl });
      const missing = [!pl.street && 'street', !pl.city && 'city', !pl.phone && 'phone', !pl.tz && 'time zone'].filter(Boolean) as string[];
      return { t: `Filled in what I could read.${missing.length ? ` Still missing: ${missing.join(', ')}.` : ' Looks complete.'}`, fills: ['Address and hours'] };
    }
    return null;
  }

  async function send(text: string) {
    text = (text || '').trim();
    if (!text || busy) return;
    const s0 = get();
    up({ chat: [...s0.chat, { r: 'u', t: text }] });
    setTxt('');

    // Deterministic paths first — instant, no typing indicator.
    const structured = tryStructured(text) || localFaq(text, get());
    if (structured) {
      up({ chat: [...get().chat, { r: 'a', t: structured.t, fills: structured.fills }] });
      return;
    }

    if (!authed()) {
      up({ chat: [...get().chat, { r: 'a', t: 'I can answer about pricing, the trial and integrations right here. Once your account is created, ask me anything — or book a call at the top and a person helps.' }] });
      return;
    }

    // The real assistant — same endpoint as the dashboard chat.
    setBusy(true);
    try {
      const history = get().chat.slice(-12, -1).map((m: ChatMsg) => ({ role: m.r === 'u' ? ('user' as const) : ('assistant' as const), content: m.t }));
      const res = await sendChatMessage(text, { type: 'onboarding', currentPage: STEP_KEYS[get().step] }, history);
      if (res.success && res.data?.response) {
        up({ chat: [...get().chat, { r: 'a', t: res.data.response.trim() || 'Done.' }] });
      } else {
        up({ chat: [...get().chat, { r: 'a', t: 'I couldn’t reach the assistant just now. You can keep going, or book a 15-minute call at the top and a person will help.' }] });
      }
    } catch {
      up({ chat: [...get().chat, { r: 'a', t: 'I couldn’t reach the assistant just now. You can keep going, or book a 15-minute call at the top and a person will help.' }] });
    }
    setBusy(false);
  }

  const chips = CHIPS[s.step] || [];
  return (
    <div className="as">
      {header}
      <div className="as-log" ref={log}>
        {s.chat.map((m, i) => (
          <div key={i} className={'msg ' + m.r}>
            <div className="bub">{md(m.t)}</div>
            {m.fills && m.fills.length ? <div className="fills">{m.fills.filter(Boolean).map((f) => <span key={f}><Ck s={10} />{f}</span>)}</div> : null}
          </div>
        ))}
        {busy ? <div className="msg a"><div className="bub typing"><i></i><i></i><i></i></div></div> : null}
      </div>
      <div className="as-chips">{chips.map(([l, pre]) => (
        <button key={l} onClick={() => { if (pre) { setTxt(pre); setTimeout(() => ta.current && ta.current.focus(), 30); } else { void send(l); } }}>{l}</button>
      ))}</div>
      <form className="as-in" onSubmit={(e) => { e.preventDefault(); void send(txt); }}>
        <textarea
          ref={ta}
          rows={txt.includes('\n') ? 4 : 1}
          value={txt}
          onChange={(e) => setTxt(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void send(txt); } }}
          placeholder="Ask, or paste anything"
          aria-label="Message Mise"
        />
        <button className="btn btn-ink btn-sm" disabled={!txt.trim() || busy}>Send</button>
      </form>
      {onClose ? <button className="as-x" onClick={onClose} aria-label="Close">×</button> : null}
    </div>
  );
}
